// Driver holiday-conflict probe used by the diary + book-in pages.
//
// Reuses the existing `GET /api/v1/drivers/:uuid/holidays?date_from=&date_to=`
// endpoint (HANDOFF — "the source of truth for entity-scoped reads"). No new
// server route is needed: the existing endpoint already does tenant scoping
// (404 cross-tenant), date-range overlap filtering, and an `has_holidays`
// flag that excludes cancelled rows.
//
// `current_status` is derived client-side from the row's stored status + its
// date range vs. today, mirroring `holidays.service.deriveCurrentStatus` on
// the server.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import { normalisePeriod, type HolidayPeriod } from '../lib/holidayPeriod';

export type HolidayStoredStatus = 'active' | 'cancelled' | 'completed';
export type HolidayCurrentStatus = 'upcoming' | 'ongoing' | 'completed' | 'cancelled';

export interface DriverHolidayItem {
  start_date: string;
  end_date: string;
  /** FULL day, or AM / PM half-day only. */
  period: HolidayPeriod;
  total_days: number;
  notes: string | null;
  status: HolidayStoredStatus;
  current_status: HolidayCurrentStatus;
}

export interface DriverHolidayCheckResult {
  hasHoliday: boolean;
  holidays: DriverHolidayItem[];
  isLoading: boolean;
  isError: boolean;
}

interface ServerHolidayRow {
  id: number;
  uuid: string;
  holiday_start: string;
  holiday_end: string;
  period?: HolidayPeriod;
  total_days: number;
  status: HolidayStoredStatus;
  notes: string | null;
}

interface ServerResponse {
  holidays: ServerHolidayRow[];
  has_holidays: boolean;
  holiday_dates: string[];
}

const STALE_MS = 5 * 60 * 1000;

function todayLocalIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function deriveCurrentStatus(
  stored: HolidayStoredStatus,
  startIso: string,
  endIso: string,
  todayIso: string,
): HolidayCurrentStatus {
  if (stored === 'cancelled') return 'cancelled';
  if (endIso < todayIso) return 'completed';
  if (startIso <= todayIso && endIso >= todayIso) return 'ongoing';
  return 'upcoming';
}

function rangesOverlap(
  rangeStart: string,
  rangeEnd: string,
  holidayStart: string,
  holidayEnd: string,
): boolean {
  return !(holidayEnd < rangeStart || holidayStart > rangeEnd);
}

export function useDriverHolidayCheck(
  driverEncId: string | null,
  startDate: string | null,
  endDate: string | null,
): DriverHolidayCheckResult {
  const enabled =
    typeof driverEncId === 'string' &&
    driverEncId.length > 0 &&
    typeof startDate === 'string' &&
    startDate.length > 0 &&
    typeof endDate === 'string' &&
    endDate.length > 0;

  const query = useQuery<DriverHolidayItem[]>({
    queryKey:
      enabled && driverEncId && startDate && endDate
        ? queryKeys.drivers.holidayCheck(driverEncId, startDate, endDate)
        : (['driver-holiday', null] as const),
    enabled,
    staleTime: STALE_MS,
    gcTime: STALE_MS,
    queryFn: async () => {
      if (!driverEncId || !startDate || !endDate) return [];
      const res = await apiClient.get<ServerResponse>(
        endpoints.drivers.holidays(driverEncId),
        { params: { date_from: startDate, date_to: endDate } },
      );
      const todayIso = todayLocalIso();
      return res.data.holidays
        .filter((h) => h.status !== 'cancelled')
        .filter((h) => rangesOverlap(startDate, endDate, h.holiday_start, h.holiday_end))
        .map<DriverHolidayItem>((h) => ({
          start_date: h.holiday_start,
          end_date: h.holiday_end,
          period: normalisePeriod(h.period),
          total_days: h.total_days,
          notes: h.notes,
          status: h.status,
          current_status: deriveCurrentStatus(h.status, h.holiday_start, h.holiday_end, todayIso),
        }));
    },
  });

  if (!enabled) {
    return { hasHoliday: false, holidays: [], isLoading: false, isError: false };
  }
  const holidays = query.data ?? [];
  return {
    hasHoliday: holidays.length > 0,
    holidays,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
